import React, { useState, useRef, useEffect } from 'react';
import { SUPPORT_PHONE_DISPLAY } from '../utils/contactInfo';

const FALLBACK_REPLY = `Sorry, I am having trouble connecting. Please email support@quotebid.io or call ${SUPPORT_PHONE_DISPLAY}!`;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SupportChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: "Hi! I'm Quinn, QuoteBid's support assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch('https://quotebid-backend-production.up.railway.app/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.filter(m => m.role !== 'assistant' || messages.indexOf(m) !== 0),
        }),
      });

      const data = await response.json();
      if (!response.ok || typeof data.reply !== 'string' || !data.reply.trim()) {
        setMessages(prev => [...prev, { role: 'assistant', content: FALLBACK_REPLY }]);
        return;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: FALLBACK_REPLY }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <div style={styles.fab} onClick={() => setIsOpen(true)}>
        <span style={styles.fabIcon}>💬</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.headerIcon}>🤖</span>
          <div>
            <div style={styles.headerTitle}>Quinn</div>
            <div style={styles.headerSubtitle}>QuoteBid Support</div>
          </div>
        </div>
        <button style={styles.closeBtn} onClick={() => setIsOpen(false)}>✕</button>
      </div>

      <div style={styles.messages}>
        {messages.map((msg, i) => (
          <div key={i} style={msg.role === 'user' ? styles.userMsg : styles.botMsg}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div style={styles.botMsg}>
            <span style={styles.typing}>Quinn is typing...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputArea}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask Quinn a question..."
          style={styles.input}
          disabled={loading}
        />
        <button onClick={sendMessage} disabled={loading} style={styles.sendBtn}>
          Send
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  fab: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#3498db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 9999,
  },
  fabIcon: {
    fontSize: '28px',
  },
  container: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '360px',
    height: '500px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column' as const,
    zIndex: 9999,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#2c3e50',
    padding: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerIcon: {
    fontSize: '24px',
  },
  headerTitle: {
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  headerSubtitle: {
    color: '#bdc3c7',
    fontSize: '12px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
  },
  messages: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '15px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  userMsg: {
    alignSelf: 'flex-end',
    backgroundColor: '#3498db',
    color: 'white',
    padding: '10px 14px',
    borderRadius: '15px 15px 0 15px',
    maxWidth: '80%',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  botMsg: {
    alignSelf: 'flex-start',
    backgroundColor: '#f0f0f0',
    color: '#2c3e50',
    padding: '10px 14px',
    borderRadius: '15px 15px 15px 0',
    maxWidth: '80%',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  typing: {
    fontStyle: 'italic',
    color: '#7f8c8d',
  },
  inputArea: {
    padding: '10px',
    borderTop: '1px solid #eee',
    display: 'flex',
    gap: '8px',
  },
  input: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '20px',
    fontSize: '14px',
    outline: 'none',
  },
  sendBtn: {
    padding: '10px 16px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    cursor: 'pointer',
  },
};

export default SupportChat;